/**
 * The three things every admin pane does, done once.
 *
 * Each pane fetches on mount, shows something while it waits, and has to say so
 * when the server refuses. Seven copies of that would be seven slightly
 * different spellings of "Loading…", and — the part that actually matters —
 * seven chances to swallow an error and render an empty table, which is
 * indistinguishable from a table that is genuinely empty.
 */
import { ref, type Ref } from 'vue';

import { ApiError } from '@/net/api';

export interface PaneState<T> {
  data: Ref<T | null>;
  loading: Ref<boolean>;
  error: Ref<string | null>;
  /** Runs the loader again. Safe to call from a button. */
  reload: () => Promise<void>;
  /**
   * Runs a mutation, then reloads. Errors land in `error` rather than throwing,
   * because a failed ban is a message on the screen, not an unhandled rejection
   * in the console.
   */
  act: (fn: () => Promise<unknown>) => Promise<void>;
}

/**
 * `403` gets its own wording.
 *
 * The panel renders on a client-side `isAdmin` hint while the API checks the
 * account's role on every request — so "you are not an administrator" is a
 * state a rendered panel can genuinely be in, and "Request failed" would send
 * somebody hunting for a bug that is really a permission.
 */
function describe(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403 || error.status === 401) {
      return 'The server refused that — this account is not an administrator.';
    }
    return error.message || `Request failed (${error.status}).`;
  }
  return 'Could not reach the server.';
}

export function usePane<T>(load: () => Promise<T>): PaneState<T> {
  const data = ref<T | null>(null) as Ref<T | null>;
  const loading = ref(false);
  const error = ref<string | null>(null);

  async function reload(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      data.value = await load();
    } catch (caught) {
      error.value = describe(caught);
    } finally {
      loading.value = false;
    }
  }

  async function act(fn: () => Promise<unknown>): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      await fn();
      data.value = await load();
    } catch (caught) {
      error.value = describe(caught);
    } finally {
      loading.value = false;
    }
  }

  void reload();
  return { data, loading, error, reload, act };
}

/** `1234` cents as `12.34`. Money is stored in cents everywhere in this API. */
export function money(cents: number, currency = ''): string {
  return `${(cents / 100).toFixed(2)}${currency ? ` ${currency}` : ''}`;
}

/** A date a person can scan, in their own timezone. */
export function when(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}
